import React, { useRef, useEffect, useState, useCallback } from 'react';
import { EditorState } from 'draft-js';
import { createStore } from '@draft-js-plugins/utils';
import { AlignBlockDefaultButton, AlignBlockLeftButton, AlignBlockCenterButton, AlignBlockRightButton } from '@draft-js-plugins/buttons';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var _excluded = ["blockProps", "block", "style"];
var getDisplayName = function getDisplayName(WrappedComponent) {
  var component = WrappedComponent.WrappedComponent || WrappedComponent;
  return component.displayName || component.name || 'Component';
};
var createDecorator = function createDecorator(_ref) {
  var store = _ref.store;
  return function (WrappedComponent) {
    var BlockAlignmentDecorator = /*#__PURE__*/React.forwardRef(function (_ref2, _ref3) {
      var blockProps = _ref2.blockProps,
        block = _ref2.block,
        style = _ref2.style,
        elementProps = _objectWithoutPropertiesLoose(_ref2, _excluded);
      var wrapper = useRef();
      useEffect(function () {
        if (blockProps.isFocused && blockProps.isCollapsedSelection) {
          var boundingRect = wrapper.current.getBoundingClientRect();
          store.updateItem('setAlignment', blockProps.setAlignment);
          store.updateItem('alignment', blockProps.alignment);
          store.updateItem('boundingRect', boundingRect);
          store.updateItem('visibleBlock', block.getKey()); // Only set visibleBlock to null in case it's the current one. This is important
          // in case the focus directly switches from one block to the other. Then the
          // Alignment tool should not be hidden, but just moved.
        } else if (store.getItem('visibleBlock') === block.getKey()) {
          store.updateItem('visibleBlock', null);
        }
        return function () {
          store.updateItem('visibleBlock', null);
        };
      }, [blockProps.isFocused, blockProps.isCollapsedSelection, store]);
      var alignment = blockProps.alignment;
      var newStyle = style;
      if (alignment === 'left') {
        newStyle = _extends({}, style, {
          "float": 'left'
        });
      } else if (alignment === 'right') {
        newStyle = _extends({}, style, {
          "float": 'right'
        });
      } else if (alignment === 'center') {
        newStyle = _extends({}, style, {
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'block'
        });
      }
      return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, elementProps, {
        block: block,
        blockProps: blockProps,
        style: newStyle,
        ref: function ref(node) {
          wrapper.current = node;
          if (typeof _ref3 === 'function') {
            _ref3(node);
          } else if (_ref3) {
            // eslint-disable-next-line no-param-reassign
            _ref3.current = node;
          }
        }
      }));
    });
    BlockAlignmentDecorator.displayName = "Alignment(" + getDisplayName(WrappedComponent) + ")"; // eslint-disable-next-line @typescript-eslint/no-explicit-any

    BlockAlignmentDecorator.WrappedComponent = WrappedComponent.WrappedComponent || WrappedComponent;
    return BlockAlignmentDecorator;
  };
};

/* eslint-disable react/no-array-index-key */

function getRelativeParent(element) {
  if (!element) {
    return null;
  }
  var position = window.getComputedStyle(element).getPropertyValue('position');
  if (position !== 'static') {
    return element;
  }
  return getRelativeParent(element.parentElement);
}
function AlignmentTool(_ref) {
  var store = _ref.store,
    theme = _ref.theme;
  var _useState = useState({}),
    position = _useState[0],
    setPosition = _useState[1];
  var _useState2 = useState(null),
    alignment = _useState2[0],
    setAlignment = _useState2[1];
  var toolbar = useRef(null);
  var ref = useRef();
  var onVisibilityChanged = useCallback(function (visibleBlock) {
    var clear = setTimeout(function () {
      var newPosition;
      var boundingRect = store.getItem('boundingRect');
      if (visibleBlock && boundingRect) {
        var relativeParent = getRelativeParent(toolbar.current.parentElement);
        var toolbarHeight = toolbar.current.clientHeight;
        var relativeRect = relativeParent ? relativeParent.getBoundingClientRect() : document.body.getBoundingClientRect();
        newPosition = {
          top: boundingRect.top - relativeRect.top - toolbarHeight,
          left: boundingRect.left - relativeRect.left + boundingRect.width / 2,
          transform: 'translate(-50%) scale(1)',
          transition: 'transform 0.15s cubic-bezier(.3,1.2,.2,1)'
        };
      } else {
        newPosition = {
          transform: 'translate(-50%) scale(0)'
        };
      }
      var newAlignment = store.getItem('alignment') || 'default';
      setAlignment(newAlignment);
      setPosition(newPosition);
      ref.current = undefined;
    }, 0);
    ref.current = clear;
  }, []);
  var onAlignmentChange = useCallback(function (value) {
    if (value) {
      setAlignment(value);
    }
  }, []);
  useEffect(function () {
    return function () {
      //clear timeout on unmount
      if (ref.current) {
        clearTimeout(ref.current);
      }
    };
  }, []);
  useEffect(function () {
    store.subscribeToItem('visibleBlock', onVisibilityChanged);
    store.subscribeToItem('alignment', onAlignmentChange);
    return function () {
      store.unsubscribeFromItem('visibleBlock', onVisibilityChanged);
      store.unsubscribeFromItem('alignment', onAlignmentChange);
    };
  }, [onVisibilityChanged, onAlignmentChange]);
  var defaultButtons = [AlignBlockDefaultButton, AlignBlockLeftButton, AlignBlockCenterButton, AlignBlockRightButton];
  return /*#__PURE__*/React.createElement("div", {
    className: theme.alignmentToolStyles.alignmentTool,
    style: position,
    ref: toolbar
  }, defaultButtons.map(function (Button, index) {
    return /*#__PURE__*/React.createElement(Button
    /* the index can be used here as the buttons list won't change */, {
      key: index,
      alignment: alignment,
      setAlignment: store.getItem('setAlignment'),
      theme: theme.buttonStyles
    });
  }));
}
var buttonStyles = {
  buttonWrapper: "b1qfpj3o",
  button: "bgspekh",
  active: "autuw9p"
};
var alignmentToolStyles = {
  alignmentTool: "awlhfjh"
};
var defaultTheme = {
  buttonStyles: buttonStyles,
  alignmentToolStyles: alignmentToolStyles
};
var createSetAlignment = function createSetAlignment(contentBlock, _ref) {
  var getEditorState = _ref.getEditorState,
    setEditorState = _ref.setEditorState;
  return function (data) {
    var entityKey = contentBlock.getEntityAt(0);
    if (entityKey) {
      var _editorState = getEditorState();
      var contentState = _editorState.getCurrentContent();
      contentState.mergeEntityData(entityKey, _extends({}, data));
      setEditorState(EditorState.forceSelection(_editorState, _editorState.getSelection()));
    }
  };
};
var index = function index(config) {
  if (config === void 0) {
    config = {};
  }
  var store = createStore({
    isVisible: false
  });
  var _config = config,
    _config$theme = _config.theme,
    theme = _config$theme === void 0 ? defaultTheme : _config$theme;
  var DecoratedAlignmentTool = function DecoratedAlignmentTool() {
    return /*#__PURE__*/React.createElement(AlignmentTool, {
      store: store,
      theme: theme
    });
  };
  return {
    initialize: function initialize(_ref2) {
      var getReadOnly = _ref2.getReadOnly,
        getEditorState = _ref2.getEditorState,
        setEditorState = _ref2.setEditorState;
      store.updateItem('getReadOnly', getReadOnly);
      store.updateItem('getEditorState', getEditorState);
      store.updateItem('setEditorState', setEditorState);
    },
    decorator: createDecorator({
      store: store
    }),
    blockRendererFn: function blockRendererFn(contentBlock, _ref3) {
      var getEditorState = _ref3.getEditorState,
        setEditorState = _ref3.setEditorState;
      var entityKey = contentBlock.getEntityAt(0);
      var contentState = getEditorState().getCurrentContent();
      var alignmentData = entityKey ? contentState.getEntity(entityKey).getData() : {};
      return {
        props: {
          alignment: alignmentData.alignment || 'default',
          setAlignment: createSetAlignment(contentBlock, {
            getEditorState: getEditorState,
            setEditorState: setEditorState
          })
        }
      };
    },
    AlignmentTool: DecoratedAlignmentTool
  };
};
export { index as default };